<template>
    <form
        :key="formKey"
        :action="formAction"
        method="POST"
        class="search-form"
        :class="searchFormClass"
        @submit.prevent="submitForm"
        @click="onClickHandler"
        @keydown="onKeyboardHandler"
    >
        <div v-if="showHotelToggler" class="search-form__control search-form__control--hotel-toggler">
            <HotelToggler v-model:hotel-return="formData.hotelReturn" />
        </div>

        <div v-if="showAirport" class="search-form__control search-form__control--airport" data-testid="airport-select">
            <SearchAirport
                :key="airportInputKey"
                name="search"
                :label="$t(`searchWidget.${activeTab}.airportLabel`)"
                :placeholder="$t(`searchWidget.${activeTab}.airportPlaceholder`)"
                :items="airports.airportList"
                :with-countries="airports.withCountries"
                :value="formData.location"
                :error="v$.location.$error"
                :error-message="errorMessages.location"
                ref="searchAirport"
                @update:value="(value: any) => updateLocation(value)"
            />
        </div>

        <div class="search-form__control search-form__control--entryDate" data-testid="entry-date">
            <DatePickerWrapper
                v-model:dropdown-opened="isEntryDatePickerOpened"
                date-label="Date"
                :min-date="currentDate()"
                :date="formData.dates.start.date"
                :error="v$.dates.start.date.$error"
                :error-message="errorMessages.dates.start.date"
                :label="$t(`searchWidget.${activeTab}.entryDateLabel`)"
                :label-mobile="activeTab === TABS.FASTTRACK ? $t(`searchWidget.date`) : ''"
                name="entryDate"
                :form-active="formActive"
                ref="entryDatePicker"
                @date-change="onStartDateChange"
                @blur="() => {}"
                @focus="() => {}"
            />
        </div>

        <div v-if="showEntryTime" class="search-form__control search-form__control--entryTime" data-testid="entry-time">
            <TimePickerWrapper
                v-model:dropdown-opened="isEntryTimePickerOpened"
                :date="formData.dates.start.date"
                :time="formData.dates.start.time"
                :min-time="minEntryTime"
                :error="v$.dates.start.time.$error"
                :error-message="errorMessages.dates.start.time"
                :label="$t(`searchWidget.${activeTab}.entryTimeLabel`)"
                :label-desktop="$t(`searchWidget.${activeTab}.entryDesktopTimeLabel`)"
                :label-mobile="$t(`searchWidget.${activeTab}.entryMobileTimeLabel`)"
                :is-entry="true"
                :time-picker-step="blok.timePickerSteps"
                :time-zone="partnerTimeZone"
                ref="entryTimePicker"
                @time-change="onStartTimeChange"
            />
        </div>

        <div v-if="showExitDate" class="search-form__control search-form__control--exitDate" data-testid="exit-date">
            <DatePickerWrapper
                v-model:dropdown-opened="isExitDatePickerOpened"
                date-label="Date"
                :date="formData.dates.end.date"
                :min-date="formData.dates.start.date ? formData.dates.start.date : currentDate()"
                :error="v$.dates.end.date.$error"
                :error-message="errorMessages.dates.end.date"
                :label="$t(`searchWidget.${activeTab}.exitDateLabel`)"
                :label-mobile="activeTab === TABS.FASTTRACK ? $t(`searchWidget.date`) : ''"
                name="exitDate"
                :calendar-position="exitDateCalendarPosition"
                :form-active="formActive"
                ref="exitDatePicker"
                @date-change="onEndDateChange"
                @blur="() => {}"
                @focus="() => {}"
            />
        </div>

        <div v-if="showExitTime" class="search-form__control search-form__control--exitTime" data-testid="exit-time">
            <TimePickerWrapper
                v-model:dropdown-opened="isExitTimePickerOpened"
                :date="formData.dates.end.date"
                :time="formData.dates.end.time"
                :min-time="minExitTime"
                :error="v$.dates.end.time.$error"
                :error-message="errorMessages.dates.end.time"
                :label="$t(`searchWidget.${activeTab}.exitTimeLabel`)"
                :label-desktop="$t(`searchWidget.${activeTab}.exitDesktopTimeLabel`)"
                :label-mobile="$t(`searchWidget.${activeTab}.exitMobileTimeLabel`)"
                :time-picker-step="blok.timePickerSteps"
                :time-zone="partnerTimeZone"
                ref="exitTimePicker"
                @time-change="onEndTimeChange"
            />
        </div>

        <div
            v-if="showTerminalsSelector"
            class="search-form__control search-form__control--terminal"
            data-testid="terminal-dropdown"
        >
            <span class="search-form__control-label">
                {{ $t(`searchWidget.${activeTab}.terminalLabel`) }}
            </span>
            <DropdownWrapper
                v-model="formData.terminal"
                v-model:dropdown-opened="isTerminalDropDownOpened"
                v-on-click-outside="() => (isTerminalDropDownOpened = false)"
                match-width
                required
                :label="$t('searchWidget.terminalPlaceholder')"
                :error="v$.terminal.id.$error"
                :error-message="errorMessages.terminal"
                :items="availableTerminals"
                ref="terminal"
                @update:model-value="(value: any) => updateTerminal(value)"
            />
        </div>

        <div
            v-if="showGuestsSelector"
            class="search-form__control search-form__control--guests"
            data-testid="guests-dropdown"
        >
            <CustomersSelectorWrapper
                v-model:dropdown-opened="isCustomersSelectorOpened"
                :customers="formData.customers"
                :label="$t(`searchWidget.${activeTab}.guestLabel`)"
                ref="customersSelector"
                @update-customers="updateCustomers"
            />
        </div>

        <div v-if="showRoomsSelector" class="search-form__control search-form__control--rooms">
            <span class="search-form__control-label">
                {{ $t(`searchWidget.${activeTab}.roomsLabel`) }}
            </span>
            <DropdownWrapper
                v-model="formData.rooms"
                v-model:dropdown-opened="isRoomsDropDownOpened"
                v-on-click-outside="() => (isRoomsDropDownOpened = false)"
                match-width
                required
                label="Select"
                :error="v$.rooms.$error"
                :error-message="errorMessages.rooms"
                :items="roomTypeList"
                @update:model-value="(value: any) => updateRooms(value)"
            />
        </div>

        <div
            v-if="showDiscount"
            class="search-form__control search-form__control--discount"
            data-testid="discount-code"
        >
            <SearchInput
                ref="discountWrapper"
                :label="$t(`searchWidget.${activeTab}.discountLabel`)"
                :placeholder="$t(`searchWidget.${activeTab}.discountPlaceholder`)"
                name="promocode"
                :value="formData.discount ?? ''"
                class="flex-1 !p-0"
                @update:value="(value: any) => updateDiscount(value)"
            />
        </div>

        <slot name="formLegacyFields"></slot>

        <button
            type="submit"
            :disabled="!isHydrated || blok.formDisabled"
            class="search-submit"
            :class="{ 'search-submit--disabled-dark': blok.formDisabled }"
        >
            {{ buttonText }}
        </button>
        <Teleport v-if="teleportAvailable && blok.promoDiscount" to="#teleports">
            <UiModalsDiscountPromptModal :blok="discountModalProps" />
        </Teleport>
    </form>
</template>

<script setup lang="ts">
import { useVuelidate } from '@vuelidate/core';
import { vOnClickOutside } from '@vueuse/components';
import { helpers, minLength, required } from '@vuelidate/validators';
import { useTabbedSearchStore } from '@nuxt-monorepo/base/app/store/tabbedSearch';
import { useIsMobileView } from '@nuxt-monorepo/ui/composables/isMobileView';
import screens from '#tailwind-config/theme/screens';
const { $useLDClient } = useNuxtApp();
const { t } = useI18n();

import type { ITabbedSearchboxForm } from '@nuxt-monorepo/ui/types/components/TabbedSearchbox';

import {
    differenceInDays,
    parse,
    addMinutes,
    isAfter,
    isBefore,
    parseISO,
    isEqual,
    utcToZonedTime,
    startOfToday,
    add,
    format,
} from './date-utilities';
import { terminalsList, roomTypeList, TABS } from './constants';

// Traffic Splitter
import {
    isMigrationActive,
    checkMigrationVariant,
    redirectToParkingPropel,
    redirectToLoungePropel,
    redirectToFastTrackPropel,
} from './traffic-splitter';
import { useModalPrompt } from '@nuxt-monorepo/ui/composables/useModalPrompt';
import { useGlobalStore } from '@nuxt-monorepo/base/app/store/global';

const globalStore = useGlobalStore();

// Components
const SearchAirport = defineAsyncComponent({ loader: async () => await import('./components/SearchAirport.vue') });
const CustomersSelectorWrapper = defineAsyncComponent({
    loader: async () => await import('./components/CustomersSelector/CustomersSelectorWrapper.vue'),
});
const TimePickerWrapper = defineAsyncComponent({
    loader: async () => await import('./components/TimePicker/TimePickerWrapper.vue'),
});
const DropdownWrapper = defineAsyncComponent({
    loader: async () => await import('./components/Common/DropdownWrapper.vue'),
});
const DatePickerWrapper = defineAsyncComponent({
    loader: async () => await import('./components/DatePicker/DatePickerWrapper.vue'),
});
const SearchInput = defineAsyncComponent({ loader: async () => await import('./components/SearchInput.vue') });
const HotelToggler = defineAsyncComponent({ loader: async () => await import('./components/HotelToggler.vue') });

// Init
const {
    blok,
    airports,
    ports = [],
    terminals = terminalsList,
    formAction,
    activeTab = 'airport-parking',
    propelPartners,
} = defineProps<ITabbedSearchboxForm>();

const emit = defineEmits(['submitForm', 'showMultipleTabs']);

const isHydrated = ref(false);
const formKey = ref(1);
const airportInputKey = ref(1);
const partnerTimeZone = blok.timeZone || 'Europe/London';
const formActive = ref(false);

const tabbedSearchStore = useTabbedSearchStore();

const { isMobileView } = useIsMobileView(screens.lg);

// Types of tabs / env
const isLounges = computed(() => activeTab === TABS.AIRPORT_LOUNGES);
const isParking = computed(() => activeTab === TABS.AIRPORT_PARKING);
const isPortParking = computed(() => activeTab === TABS.PORT_PARKING);
const isFastTrack = computed(() => activeTab === TABS.FASTTRACK);
const isL4Folder = computed(() => STORYBLOK_FOLDER === 'l4');
const isSPSFolder = computed(() => STORYBLOK_FOLDER === 'sps');

// Migration
const config = useRuntimeConfig();
const STORYBLOK_FOLDER = config?.public?.STORYBLOK_FOLDER;

const migrationKey = computed(() => {
    let migrationKey = '';
    if (isL4Folder.value) {
        if (
            [TABS.AIRPORT_PARKING, TABS.PORT_PARKING].includes(
                activeTab as typeof TABS.AIRPORT_PARKING | typeof TABS.PORT_PARKING
            )
        ) {
            migrationKey = '_parking-only';
        }
    }
    if (isSPSFolder.value) {
        if (isParking.value) {
            migrationKey = '_airportparking';
        }
        if (isLounges.value) {
            migrationKey = '_airportlounge';
        }
    }
    return migrationKey;
});

// For lounges end date can be empty
const dateForTrafficSplitter = computed(() =>
    isLounges.value ? formData.value?.dates?.start?.date : formData.value?.dates?.end?.date
);

const formAirportFromStoryblok = inject('airport', '');
const formAirportFromApiProduct = inject('airportFromApiProduct', '');
const propelProductSKU = inject<string>('propelProductSKU', '');

const availableTerminals = computed(() => {
    const activeAirportId = +formData.value.location.airportId;
    const availableTerminals = terminals.filter((terminal) => terminal.airportId === activeAirportId);
    if (availableTerminals.length && withDontKnowTerminalOption.value) {
        availableTerminals.push({ id: 'dontKnow', name: t('searchWidget.dontKnowOption'), airportId: 0 });
    }
    return availableTerminals;
});

const defaultAirport = computed(() => {
    let injectedCode = '';
    if (formAirportFromStoryblok) {
        injectedCode = formAirportFromStoryblok?.content
            ? formAirportFromStoryblok?.content?.code?.toUpperCase()
            : formAirportFromStoryblok?.code?.toUpperCase();
    } else if (formAirportFromApiProduct) {
        injectedCode = formAirportFromApiProduct;
    }
    const defaultCode = blok.defaultAirportCode || injectedCode;
    return (
        airports.airportList.flat().find((airport) => airport.code === defaultCode) ||
        ports.find((port) => port.code === defaultCode)
    );
});

const defaultTerminal = computed(() => {
    const terminalString = blok.defaultTerminal;
    if (!terminalString) return null;
    const [airportId, terminalId] = terminalString.split('|');
    return terminals.find(
        (terminal) =>
            defaultAirport?.value?.airportId == airportId &&
            terminal?.airportId === Number(airportId) &&
            terminal?.id === Number(terminalId)
    );
});

// LaunchDarkly
const withDontKnowTerminalOption = ref(true);
const route = useRoute();

let ldClient;
onMounted(async () => {
    isHydrated.value = true;

    ldClient = $useLDClient();
    try {
        await ldClient.waitForInitialization();

        const anonymousUserContext = {
            kind: 'user',
            anonymous: true,
        };
        const airportContext = {
            kind: 'airport',
            key: `${STORYBLOK_FOLDER}-airport-code`,
            code: defaultAirport?.value?.code,
        };
        const fullPathWithoutQuery = route.fullPath.split('?')[0];
        const pageUrl = fullPathWithoutQuery.slice(1).replaceAll('/', '-');
        const pageContext = {
            kind: 'page',
            key: `${STORYBLOK_FOLDER}-page`,
            page: pageUrl,
        };

        const multiContext = {
            kind: 'multi',
            user: anonymousUserContext,
            airport: airportContext,
            page: pageContext,
        };

        await ldClient.identify(multiContext);

        withDontKnowTerminalOption.value =
            ldClient.variation('CRO_839_TERMINALS_OPTIONS_IN_SEARCH_FORM', true) !== undefined
                ? ldClient.variation('CRO_839_TERMINALS_OPTIONS_IN_SEARCH_FORM', true)
                : true;
        emit('showMultipleTabs', ldClient.variation('CRO_1030_MULTI_TABS_FORM', true));
        exerimentQueryParamUpdate();
    } catch (err) {
        console.log('LaunchDarkly error');
    }
});

const exerimentQueryParamUpdate = () => {
    const router = useRouter();
    if (
        ldClient?.variationDetail('CRO_1030_MULTI_TABS_FORM')?.reason?.inExperiment === true &&
        ldClient?.variation('CRO_1030_MULTI_TABS_FORM', true) === true &&
        route.query['test'] !== 'cro-1126'
    ) {
        router.replace({
            query: {
                ...route.query,
                test: 'cro-1126',
            },
        });
    }
};

// Dropdowns open state
const isTerminalDropDownOpened = ref(false);
const isEntryDatePickerOpened = ref(false);
const isEntryTimePickerOpened = ref(false);
const isExitDatePickerOpened = ref(false);
const isExitTimePickerOpened = ref(false);
const isCustomersSelectorOpened = ref(false);
const isRoomsDropDownOpened = ref(false);
const discountWrapper = ref(null);

// Fields visibility
const showAirport = computed(() => blok.showAirport);
const showEntryTime = computed(() => activeTab !== TABS.HOTEL_AND_PARKING);
const showExitDate = computed(
    () =>
        ![TABS.AIRPORT_LOUNGES, TABS.FASTTRACK].includes(
            activeTab as typeof TABS.AIRPORT_LOUNGES | typeof TABS.FASTTRACK
        )
);
const showExitTime = computed(
    () =>
        ![TABS.HOTEL_AND_PARKING, TABS.FASTTRACK, TABS.AIRPORT_LOUNGES].includes(
            activeTab as typeof TABS.AIRPORT_LOUNGES | typeof TABS.FASTTRACK | typeof TABS.HOTEL_AND_PARKING
        )
);
const showDiscount = computed(() => blok.showDiscount && !blok.formDisabled);
const showTerminalsSelector = computed(() => activeTab !== TABS.HOTEL_AND_PARKING && !!availableTerminals.value.length);
// Previously, GuestSelector was always visible by default as no showGuestSelector field existed in CMS.
// The newly added visibility control will only affect content published after this change.
const showGuestsSelector = computed(
    () => activeTab !== TABS.HOTEL_AND_PARKING && (blok.showGuestSelector === undefined || blok.showGuestSelector)
);
const showRoomsSelector = computed(() => activeTab === TABS.HOTEL_AND_PARKING);
const showHotelToggler = ref(false);

const exitDateCalendarPosition = computed(() => (blok.sectionType === 'one-line' ? 'center' : 'right'));

const buttonText = computed(() =>
    blok.formDisabled ? t('searchWidget.buttonComingSoon') : t('searchWidget.buttonSearch')
);

// Form data
const formData = ref({
    location: {
        name: defaultAirport.value ? defaultAirport.value.name : tabbedSearchStore.getAllData.location.name,
        code: defaultAirport.value ? defaultAirport.value.code : tabbedSearchStore.getAllData.location.code,
        airportId: defaultAirport.value ? defaultAirport.value.airportId : tabbedSearchStore.getAllData.location.id,
        country: defaultAirport.value ? defaultAirport.value.country : tabbedSearchStore.getAllData.location.country,
    },
    dates: {
        start: {
            date: tabbedSearchStore.getAllData.dates.start.date || '',
            time: tabbedSearchStore.getAllData.dates.start.time || '',
        },
        end: {
            date: tabbedSearchStore.getAllData.dates.end.date || '',
            time: tabbedSearchStore.getAllData.dates.end.time || '',
        },
    },
    terminal: defaultTerminal.value ? defaultTerminal.value : tabbedSearchStore.getAllData.terminal,
    customers: {
        adult: tabbedSearchStore.getAllData.passengers.adult || 0,
        child: tabbedSearchStore.getAllData.passengers.child || 0,
        children_ages: tabbedSearchStore.getAllData.passengers.children_ages || [],
    },
    rooms: tabbedSearchStore.getAllData.rooms || 0,
    discount: tabbedSearchStore.getAllData.discount || '',
    hotelReturn: tabbedSearchStore.getAllData.hotelReturn || false,
});

// Update formData from query
const { query } = useRoute();
const discountValue = (isL4Folder.value ? query.code : query.promo) ?? query.discount;

if (discountValue) {
    formData.value.discount = discountValue;
    tabbedSearchStore.updateDiscount(discountValue);
}

if (query.airport) {
    const airportFromQuery = airports.airportList
        .flat()
        .find((airport) => airport.code === query.airport.toUpperCase());
    if (airportFromQuery) {
        const locationFromQuery = {
            name: airportFromQuery.name,
            code: airportFromQuery.code,
            airportId: airportFromQuery.airportId,
            country: airportFromQuery.country,
        };
        formData.value.location = locationFromQuery;
        tabbedSearchStore.updateLocation({
            name: airportFromQuery.name,
            code: airportFromQuery.code,
            id: airportFromQuery.airportId,
            country: airportFromQuery.country,
        });
    }
}

if (query.terminal) {
    const terminalFromQuery = terminals.find((terminal) => terminal.id === +query.terminal);
    if (terminalFromQuery) {
        const terminalFromQueryData = {
            id: terminalFromQuery.id,
            name: terminalFromQuery.name,
            airportId: terminalFromQuery.name,
        };
        formData.value.terminal = terminalFromQueryData;
        tabbedSearchStore.updateTerminal(terminalFromQueryData);
    }
}

// Validation rules
const rules = computed(() => {
    const rules: any = {
        location: {
            name: {
                required: helpers.withMessage(t('searchWidget.validationError.airport'), required),
                minLength: minLength(3),
            },
        },
        dates: {
            start: {
                date: {
                    required: helpers.withMessage(t('searchWidget.validationError.start.date'), required),
                },
                time: {
                    required: helpers.withMessage(t('searchWidget.validationError.start.time'), required),
                },
            },
        },
    };
    if (
        ![TABS.AIRPORT_LOUNGES, TABS.FASTTRACK].includes(
            activeTab as typeof TABS.AIRPORT_LOUNGES | typeof TABS.FASTTRACK
        )
    ) {
        rules.dates.end = {
            date: {
                required: helpers.withMessage(t('searchWidget.validationError.end.date'), required),
            },
            time: {
                required: helpers.withMessage(t('searchWidget.validationError.end.time'), required),
            },
        };
    }
    if (showTerminalsSelector.value) {
        rules.terminal = {
            id: {
                required: helpers.withMessage(t('searchWidget.validationError.terminal'), required),
            },
        };
    }
    if (showRoomsSelector.value) {
        rules.rooms = {
            required: helpers.withMessage('Please, select rooms', required),
        };
    }
    return rules;
});

const errorMessages = computed(() => {
    return {
        location: v$.value.location.$errors.map((e) => e.$message)[0],
        dates: {
            start: {
                date: v$.value?.dates.start.date?.$errors?.map((e) => e.$message)[0],
                time: v$.value?.dates.start.time?.$errors?.map((e) => e.$message)[0],
            },
            end: {
                date: v$.value?.dates?.end?.date?.$errors?.map((e) => e.$message)[0],
                time: v$.value?.dates?.end?.time?.$errors?.map((e) => e.$message)[0],
            },
        },
        rooms: v$.value?.rooms?.$errors?.map((e) => e.$message)[0],
        terminal: v$.value?.terminal?.id?.$errors?.map((e) => e.$message)[0],
    };
});

const v$ = useVuelidate(rules, formData);

// Date / time helpers
const datesArePast = computed(() => {
    const {
        dates: {
            start: { date: startDate, time: startTime },
            end: { date: endDate, time: endTime },
        },
    } = formData.value;
    const startDateTime = `${startDate} ${startTime}`;
    const endDateTime = `${endDate} ${endTime}`;

    let currentDate = utcToZonedTime(new Date(), partnerTimeZone);
    if (isLounges.value) {
        currentDate = minEntryTime.value;
    }

    const entryDate = parseISO(startDateTime);
    const exitDate = parseISO(endDateTime);

    return (
        (!!startDate && !!startTime && isBefore(entryDate, currentDate)) ||
        (!!startDate && !!endTime && isBefore(exitDate, currentDate))
    );
});

const today = ref(new Date());
//in case if user open the page and leave it to the next day, the date should be updated
const currentDate = () => {
    if (differenceInDays(today.value, new Date()) > 0) {
        today.value = new Date();
    }
    return today.value;
};

const entryDateTimeBeforeExitDateTime = computed(() => {
    const entryDate = parseISO(`${formData.value.dates.start.date} ${formData.value.dates.start.time || ''}`);
    const exitDate = parseISO(`${formData.value.dates.end.date} ${formData.value.dates.end.time || ''}`);
    return isAfter(entryDate, exitDate) || isEqual(entryDate, exitDate);
});

const minExitTime = computed(() => {
    const minDate = new Date(new Date(formData.value.dates.start.date).toDateString());
    const endDate = new Date(new Date(formData.value.dates.end.date).toDateString());
    if (differenceInDays(minDate, endDate) !== 0) {
        return '';
    }
    return format(
        addMinutes(
            parse(
                `${formData.value.dates.end.date} ${formData.value.dates.start.time}`,
                'yyyy-MM-dd HH:mm',
                new Date()
            ),
            60
        ),
        'yyyy-MM-dd HH:mm'
    );
});

const resetExitDateTime = () => {
    formData.value.dates.end.date = '';
    formData.value.dates.end.time = '';
    tabbedSearchStore.updateEndDates('', '');
};

const resetDates = () => {
    resetExitDateTime();
    formData.value.dates.start.date = '';
    formData.value.dates.start.time = '';
    tabbedSearchStore.updateStartDates('', '');
};

const currentTimeInPartnerTimeZone = computed<string>(() => {
    return utcToZonedTime(new Date(), partnerTimeZone);
});

const minEntryTime = computed<string | Date>(() => {
    if (activeTab !== TABS.AIRPORT_LOUNGES) return '';
    let minLoungesTime;
    if (startOfToday().getTime() === startOfToday(currentTimeInPartnerTimeZone.value).getTime()) {
        //if date is today, then the time should be limited to the time slot 2 segments ahead of 1 hour from now.
        minLoungesTime = add(currentTimeInPartnerTimeZone.value, { minutes: 75 });
    } else {
        minLoungesTime = utcToZonedTime(currentTimeInPartnerTimeZone.value, partnerTimeZone);
    }
    return format(minLoungesTime, 'yyyy-MM-dd HH:mm');
});

// Update form
const updateLocation = (value) => {
    formData.value.location = value;
    tabbedSearchStore.updateLocation(value);
    selectFieldAfterLocation();
    if (!isSPSFolder.value && isMigrationActive()) {
        checkMigrationVariant(formData.value.location.code, dateForTrafficSplitter.value, migrationKey.value);
    }
    updateTerminal({
        id: '',
        name: '',
        airportId: '',
    });
};

const updateTerminal = (value) => {
    isTerminalDropDownOpened.value = false;
    formData.value.terminal = value;
    tabbedSearchStore.updateTerminal(value);
    selectFieldAfterTerminal(value);
};

const onStartDateChange = (value: any) => {
    formData.value.dates.start.date = value;
    if (datesArePast.value) {
        formData.value.dates.start.time = '';
    }
    if (entryDateTimeBeforeExitDateTime.value) {
        resetExitDateTime();
    }
    tabbedSearchStore.updateStartDates(value, formData.value.dates.start.time);
    selectFieldAfterStartDate();
};

const onStartTimeChange = (value) => {
    formData.value.dates.start.time = value;
    tabbedSearchStore.updateStartDates(formData.value.dates.start.date, value);
    if (entryDateTimeBeforeExitDateTime.value) {
        resetExitDateTime();
    }
    selectFieldAfterStartTime();
};

const onEndDateChange = (value: any) => {
    formData.value.dates.end.date = value;
    if (entryDateTimeBeforeExitDateTime.value) {
        formData.value.dates.end.time = '';
    }
    tabbedSearchStore.updateEndDates(value, formData.value.dates.end.time);
    selectFieldAfterEndDate();
    if (!isSPSFolder.value && isMigrationActive()) {
        checkMigrationVariant(formData.value.location.code, value, migrationKey.value);
    }
};

const onEndTimeChange = (value) => {
    formData.value.dates.end.time = value;
    tabbedSearchStore.updateEndDates(tabbedSearchStore.getAllData.dates.end.date, value);
    selectFieldAfterEndTime();
};

const updateCustomers = (value) => {
    formData.value.customers = value;
    tabbedSearchStore.updateQuoteFormPassengers(value);
};

const updateRooms = (value) => {
    formData.value.rooms = value;
    tabbedSearchStore.updateRooms(value);
};

const updateDiscount = (value) => {
    formData.value.discount = value;
    tabbedSearchStore.updateDiscount(value);
};

// Helpers for moving throught the form as on Propel
const selectFieldAfterLocation = () => {
    nextTick(() => {
        if (isMobileView.value && showTerminalsSelector.value && !formData.value.terminal.id) {
            if (!isKeyboardNavigation.value) {
                isTerminalDropDownOpened.value = true;
            } else {
                terminal.value.focusOnDropdown();
            }
        } else if (
            (!isMobileView.value || (isMobileView.value && !showTerminalsSelector.value)) &&
            !formData.value.dates.start.date
        ) {
            if (!isKeyboardNavigation.value) {
                isEntryDatePickerOpened.value = true;
            } else {
                entryDatePicker?.value?.focusInput();
            }
        }
    });
};

const selectFieldAfterTerminal = (value) => {
    nextTick(() => {
        if (!value.id) return;
        if (isMobileView.value && !formData.value.dates.start.date) {
            isEntryDatePickerOpened.value = true;
        } else if (!isMobileView.value && !formData.value.customers.adult) {
            isCustomersSelectorOpened.value = true;
        }
    });
};

const selectFieldAfterStartDate = () => {
    nextTick(() => {
        if (!formData.value.dates.start.time) {
            if (!isKeyboardNavigation.value) {
                isEntryTimePickerOpened.value = true;
            } else {
                entryTimePicker?.value?.focusInput();
            }
        }
    });
};

const selectFieldAfterStartTime = () => {
    nextTick(() => {
        if (!formData.value.dates.end.date) {
            if (!isKeyboardNavigation.value) {
                isExitDatePickerOpened.value = true;
            } else {
                exitDatePicker?.value?.focusInput();
            }
        }
    });
};

const selectFieldAfterEndDate = () => {
    nextTick(() => {
        if (showRoomsSelector.value && !formData.value.rooms) {
            isRoomsDropDownOpened.value = true;
            return;
        }
        if (!formData.value.dates.end.time) {
            if (!isKeyboardNavigation.value) {
                isExitTimePickerOpened.value = true;
            } else {
                exitTimePicker?.value?.focusInput();
            }
        }
    });
};

const selectFieldAfterEndTime = () => {
    nextTick(() => {
        if (!isMobileView.value && showTerminalsSelector.value && !formData.value.terminal.id) {
            isTerminalDropDownOpened.value = true;
        } else if (
            ((!isMobileView.value && !showTerminalsSelector.value) || isMobileView.value) &&
            !formData.value.customers.adult
        ) {
            if (!isKeyboardNavigation.value) {
                isCustomersSelectorOpened.value = true;
            } else {
                customersSelector.value.focusOnCustomers();
            }
        }
    });
};

// Form submission
const propelUrl = computed(() => {
    return (
        propelPartners?.find((propelPartner) => {
            return propelPartner.Country === formData.value.location?.country?.slug?.toLowerCase();
        })?.PropelUrl || ''
    );
});

const teleportAvailable = ref(false);
const discountModalProps = computed(() => ({
    discountCode: blok.discountCode,
    modalTitle: blok.modalTitle,
    modalText: blok.modalText,
    submitButton: blok.submitButton,
    cancelButton: blok.cancelButton,
    inputPlaceholder: blok.inputPlaceholder,
    useMparticle: globalStore?.global?.UseMparticle,
    signupSource: blok.signupSource,
}));

const submitForm = async (e) => {
    v$.value.$touch();
    if (!v$.value.$error) {
        if (blok.promoDiscount) {
            const userDiscount = await useModalPrompt().open();
            if (userDiscount) {
                tabbedSearchStore.updateDiscount(userDiscount);
            }
        }
        const isInExperiment =
            ldClient?.variationDetail('CRO_839_TERMINALS_OPTIONS_IN_SEARCH_FORM')?.reason?.inExperiment === true ||
            ldClient?.variationDetail('CRO_1030_MULTI_TABS_FORM')?.reason?.inExperiment === true;
        if (isInExperiment) {
            ldClient?.track('search_form_submit');
            try {
                await ldClient?.flush();
            } catch (err) {
                console.error('LaunchDarkly flushing error');
            }
        }

        const migrationCheck = !isSPSFolder.value
            ? await checkMigrationVariant(
                  formData.value.location?.code,
                  dateForTrafficSplitter.value,
                  migrationKey.value
              )
            : null;

        setTimeout(
            () => {
                if (isSPSFolder.value) {
                    if (isParking.value || isPortParking.value) {
                        redirectToParkingPropel(propelProductSKU);
                    }
                    if (isLounges.value) {
                        redirectToLoungePropel(propelProductSKU);
                    }
                    if (isFastTrack.value) {
                        redirectToFastTrackPropel(propelProductSKU);
                    }
                    return;
                } else {
                    if (
                        (isParking.value || isPortParking.value) &&
                        isMigrationActive() &&
                        migrationCheck?.inMigratedVariant.value
                    ) {
                        redirectToParkingPropel(propelProductSKU, propelUrl.value);
                        return;
                    }
                    if (isLounges.value) {
                        redirectToLoungePropel(propelProductSKU);
                        return;
                    }
                    if (isFastTrack.value) {
                        redirectToFastTrackPropel(propelProductSKU);
                        return;
                    }
                }

                emit('submitForm', { formData: formData.value, form: e });
            },
            isInExperiment ? 150 : 0
        );
    }
};

const searchFormClass = computed(() => [
    `is-${activeTab}`,
    { 'with-discount': showDiscount.value },
    { 'with-terminals': showTerminalsSelector.value },
    { 'with-airport': blok.showAirport },
    { 'with-hotel-toggler': showHotelToggler.value },
    { 'without-guest-selector': !showGuestsSelector.value },
    { 'one-line': blok.sectionType === 'one-line' },
    {
        'one-line-hero-section':
            blok.sectionType !== 'one-line' &&
            !showGuestsSelector.value &&
            !showTerminalsSelector.value &&
            !blok.formDisabled,
    },
    { 'is-disabled': blok.formDisabled },
]);

onMounted(async () => {
    formKey.value += 1;
    if (!isSPSFolder.value && isMigrationActive()) {
        await checkMigrationVariant(formData.value.location?.code, dateForTrafficSplitter.value, migrationKey.value);
    }
    if (datesArePast.value) {
        resetDates();
    }
    teleportAvailable.value = true;
});

watch(
    () => formData.value.location,
    (value) => {
        if (value?.code) {
            tabbedSearchStore.updateLocation(value);
            airportInputKey.value += 1;
        }
    },
    { deep: true, immediate: true }
);

watch(
    () => formData.value.terminal,
    (value) => {
        if (value?.id) {
            tabbedSearchStore.updateTerminal(value);
        }
    },
    { deep: true, immediate: true }
);

watch(
    () => airports.airportList,
    () => (airportInputKey.value += 1),
    { deep: true }
);

watch(
    () => activeTab,
    (oldVal, newVal) => {
        if (
            !airports.airportList
                .flat()
                .find(
                    (airport) =>
                        airport.code === formData.value.location.code && airport.name === formData.value.location.name
                )
        ) {
            formData.value.location = { name: '', code: '', airportId: '' };
            tabbedSearchStore.updateLocation({ name: '', code: '', id: '' });
        }
        airportInputKey.value += 1;

        // If time was selected for Parking and after selecting Lounges there is less then 75 minutes from now - reset entry time
        if (datesArePast.value) {
            formData.value.dates.start.time = '';
            tabbedSearchStore.updateStartDates(formData.value.dates.start.date, '');
        }
    }
);

watch(
    () => availableTerminals.value,
    (val) => {
        const terminalFromCurrentAirport = val.find(
            (terminal) => terminal.airportId === +formData.value.terminal.airportId
        );
        if (val?.length === 0 || !terminalFromCurrentAirport) {
            const emptyTerminal = {
                id: '',
                name: '',
                airportId: '',
            };
            formData.value.terminal = emptyTerminal;
            tabbedSearchStore.updateTerminal(emptyTerminal);
        }
    },
    { immediate: true }
);

watch(
    () => tabbedSearchStore.getAllData?.discount,
    (value) => {
        if (value && value !== formData.value.discount) {
            formData.value.discount = value;
        }
    },
    { immediate: true }
);

// Keyboard navigation
const isKeyboardNavigation = ref(false);

// Template refs for keyboard navigation
const searchAirport = ref();
const terminal = ref();
const entryDatePicker = ref();
const entryTimePicker = ref();
const exitDatePicker = ref();
const exitTimePicker = ref();
const customersSelector = ref();

const onClickHandler = () => {
    formActive.value = true;
    isKeyboardNavigation.value = false;
};

const onKeyboardHandler = (event: KeyboardEvent) => {
    if (event.code === 'Enter' && event.target.type !== 'submit') event?.preventDefault();
    isKeyboardNavigation.value = true;
};

const focusOnFirstElement = () => {
    searchAirport.value.focusOnInput();
};

defineExpose({ focusOnFirstElement });
</script>

<style scoped lang="postcss">
.search-form {
    @apply flex flex-wrap lg:items-end lg:grid lg:grid-cols-12 lg:grid-rows-2 gap-y-4 gap-x-2 lg:gap-2 p-6 md:p-8;
    &.with-hotel-toggler {
        grid-template-rows: auto 1fr 1fr;
    }
    &.one-line,
    &.one-line-hero-section {
        @apply px-4;
        @media (min-width: 1280px) {
            grid-template-rows: auto;
            grid-template-columns: repeat(16, 1fr);
            @apply px-4;
        }
    }
    &.one-line-hero-section {
        @apply lg:grid-rows-1;
        &.with-discount {
            @apply lg:grid-rows-2;
            &.without-guest-selector {
                @apply lg:grid-rows-1;
            }
        }
    }
    &.is-disabled {
        @apply pointer-events-none select-none;
    }

    &__control {
        @apply w-full;
        &--entryDate,
        &--exitDate,
        &--entryTime,
        &--exitTime {
            @media (max-width: 1023px) {
                width: calc(50% - 4px);
            }
        }
        &--hotel-toggler {
            @apply hidden;
            grid-area: 1 / 1 / span 1 / -1;
        }
        &--airport {
            grid-area: 1 / 1 / span 1 / span 4;
            order: 1;
            .with-hotel-toggler & {
                grid-area: 2 / 1 / span 1 / span 4;
            }
            .one-line-hero-section & {
                grid-area: 1 / 1 / span 1 / span 4;
                @media (min-width: 1280px) {
                    grid-area: 1 / 1 / span 1 / span 4;
                }
            }
            .one-line-hero-section.with-airport.with-discount & {
                grid-area: 1 / 1 / span 1 / span 4;
                @media (min-width: 1280px) {
                    grid-area: 1 / 1 / span 1 / span 3;
                }
            }
            .one-line-hero-section.is-fasttrack &,
            .one-line-hero-section.is-airport-lounges & {
                @media (min-width: 1280px) {
                    grid-area: 1 / 1 / span 1 / span 4;
                }
            }
            .one-line-hero-section.with-discount.is-fasttrack &,
            .one-line-hero-section.with-discount.is-airport-lounges & {
                @media (min-width: 1280px) {
                    grid-area: 1 / 1 / span 1 / span 4;
                }
            }
            .is-disabled & {
                grid-area: 1 / 1 / span 1 / span 4;
            }
            .is-disabled.with-terminals & {
                grid-area: 1 / 1 / span 1 / span 4;
            }
        }
        &--entryDate {
            grid-area: 1 / 1 / span 1 / span 3;
            order: 3;
            .with-airport & {
                grid-area: 1 / 5 / span 1 / span 2;
            }
            .is-airport-lounges &,
            .is-fasttrack &,
            .is-hotel-and-parking & {
                grid-area: 1 / 1 / span 1 / span 6;
            }
            .with-hotel-toggler.is-hotel-and-parking & {
                grid-area: 2 / 1 / span 1 / span 6;
            }
            .with-airport.is-airport-lounges &,
            .with-airport.is-fasttrack &,
            .with-airport.is-hotel-and-parking & {
                grid-area: 1 / 5 / span 1 / span 4;
            }
            .with-hotel-toggler.with-airport.is-hotel-and-parking & {
                grid-area: 2 / 5 / span 1 / span 4;
            }
            .one-line & {
                @media (min-width: 1280px) {
                    grid-area: 1 / 1 / span 1 / span 3;
                }
            }
            .one-line.with-discount &,
            .one-line.with-terminals & {
                @media (min-width: 1280px) {
                    grid-area: 1 / 1 / span 1 / span 2;
                }
            }
            .one-line-hero-section.with-discount & {
                grid-area: 1 / 1 / span 1 / span 2;
                @media (min-width: 1280px) {
                    grid-area: 1 / 1 / span 1 / span 3;
                }
            }
            .one-line-hero-section.with-airport & {
                grid-area: 1 / 5 / span 1 / span 2;
                @media (min-width: 1280px) {
                    grid-area: 1 / 5 / span 1 / span 2;
                }
            }
            .one-line-hero-section.with-airport.with-discount & {
                @media (min-width: 1280px) {
                    grid-area: 1 / 4 / span 1 / span 2;
                }
            }
            .one-line-hero-section.is-fasttrack &,
            .one-line-hero-section.is-airport-lounges {
                grid-area: 1 / 1 / span 1 / span 6;
            }

            .one-line-hero-section.with-discount.is-fasttrack &,
            .one-line-hero-section.with-discount.is-airport-lounges & {
                grid-area: 1 / 1 / span 1 / span 4;
                @media (min-width: 1280px) {
                    grid-area: 1 / 1 / span 1 / span 5;
                }
            }
            .one-line-hero-section.with-airport.is-fasttrack &,
            .one-line-hero-section.with-airport.is-airport-lounges & {
                grid-area: 1 / 5 / span 1 / span 4;
                @media (min-width: 1280px) {
                    grid-area: 1 / 5 / span 1 / span 5;
                }
            }
            .one-line-hero-section.with-airport.with-discount.is-fasttrack &,
            .one-line-hero-section.with-airport.with-discount.is-airport-lounges & {
                @media (min-width: 1280px) {
                    grid-area: 1 / 5 / span 1 / span 3;
                }
            }
            .is-disabled.with-airport & {
                grid-area: 1 / 5 / span 1 / span 2;
            }
            .is-disabled.with-terminals.with-airport & {
                grid-area: 1 / 5 / span 1 / span 2;
            }
            .is-disabled.one-line & {
                @media (min-width: 1280px) {
                    grid-area: 1 / 1 / span 1 / span 2;
                }
            }
        }
        &--entryTime {
            grid-area: 1 / 4 / span 1 / span 3;
            order: 4;
            .with-airport & {
                grid-area: 1 / 7 / span 1 / span 2;
            }
            .is-airport-lounges &,
            .is-fasttrack & {
                grid-area: 1 / 7 / span 1 / -1;
            }
            .is-airport-lounges.with-airport &,
            .is-fasttrack.with-airport & {
                grid-area: 1 / 9 / span 1 / -1;
            }
            .one-line & {
                @media (min-width: 1280px) {
                    grid-area: 1 / 4 / span 1 / span 3;
                }
            }
            .one-line.with-discount &,
            .one-line.with-terminals & {
                @media (min-width: 1280px) {
                    grid-area: 1 / 3 / span 1 / span 2;
                }
            }
            .one-line-hero-section.with-discount & {
                grid-area: 1 / 3 / span 1 / span 2;
                @media (min-width: 1280px) {
                    grid-area: 1 / 4 / span 1 / span 3;
                }
            }
            .one-line-hero-section.with-airport & {
                grid-area: 1 / 7 / span 1 / span 2;
                @media (min-width: 1280px) {
                    grid-area: 1 / 7 / span 1 / span 2;
                }
            }
            .one-line-hero-section.with-airport.with-discount & {
                @media (min-width: 1280px) {
                    grid-area: 1 / 6 / span 1 / span 2;
                }
            }
            .one-line-hero-section.is-fasttrack &,
            .one-line-hero-section.is-airport-lounges & {
                grid-area: 1 / 7 / span 1 / span 6;
            }
            .one-line-hero-section.with-discount.is-fasttrack &,
            .one-line-hero-section.with-discount.is-airport-lounges & {
                grid-area: 1 / 5 / span 1 / span 4;
                @media (min-width: 1280px) {
                    grid-area: 1 / 6 / span 1 / span 5;
                }
            }
            .one-line-hero-section.with-airport.is-fasttrack &,
            .one-line-hero-section.with-airport.is-airport-lounges & {
                grid-area: 1 / 9 / span 1 / span 4;
                @media (min-width: 1280px) {
                    grid-area: 1 / 10 / span 1 / span 5;
                }
            }
            .one-line-hero-section.with-airport.with-discount.is-fasttrack &,
            .one-line-hero-section.with-airport.with-discount.is-airport-lounges & {
                @media (min-width: 1280px) {
                    grid-area: 1 / 8 / span 1 / span 3;
                }
            }
            .is-disabled.with-airport & {
                grid-area: 1 / 7 / span 1 / span 2;
            }
            .is-disabled.with-terminals.with-airport & {
                grid-area: 1 / 7 / span 1 / span 2;
            }
            .is-disabled.one-line & {
                @media (min-width: 1280px) {
                    grid-area: 1 / 3 / span 1 / span 2;
                }
            }
        }
        &--exitDate {
            grid-area: 1 / 7 / span 1 / span 3;
            order: 5;
            .with-airport & {
                grid-area: 1 / 9 / span 1 / span 2;
            }
            .is-hotel-and-parking & {
                grid-area: 1 / 7 / span 1 / -1;
            }
            .with-hotel-toggler.is-hotel-and-parking & {
                grid-area: 2 / 7 / span 1 / -1;
            }
            .with-airport.is-hotel-and-parking & {
                grid-area: 1 / 9 / span 1 / -1;
            }
            .with-hotel-toggler.with-airport.is-hotel-and-parking & {
                grid-area: 2 / 9 / span 1 / -1;
            }
            .one-line & {
                @media (min-width: 1280px) {
                    grid-area: 1 / 7 / span 1 / span 3;
                }
            }
            .one-line.with-discount &,
            .one-line.with-terminals & {
                @media (min-width: 1280px) {
                    grid-area: 1 / 5 / span 1 / span 2;
                }
            }
            .one-line-hero-section.with-discount & {
                grid-area: 1 / 5 / span 1 / span 2;
                @media (min-width: 1280px) {
                    grid-area: 1 / 7 / span 1 / span 3;
                }
            }
            .one-line-hero-section.with-airport & {
                grid-area: 1 / 9 / span 1 / span 2;
                @media (min-width: 1280px) {
                    grid-area: 1 / 9 / span 1 / span 2;
                }
            }
            .one-line-hero-section.with-airport.with-discount & {
                @media (min-width: 1280px) {
                    grid-area: 1 / 8 / span 1 / span 2;
                }
            }
            .is-disabled.with-airport & {
                grid-area: 1 / 9 / span 1 / span 2;
            }
            .is-disabled.with-terminals.with-airport & {
                grid-area: 1 / 9 / span 1 / span 2;
            }
            .is-disabled.one-line & {
                @media (min-width: 1280px) {
                    grid-area: 1 / 5 / span 1 / span 2;
                }
            }
        }
        &--exitTime {
            grid-area: 1 / 10 / span 1 / -1;
            order: 6;
            .with-airport & {
                grid-area: 1 / 11 / span 1 / -1;
            }
            .one-line & {
                @media (min-width: 1280px) {
                    grid-area: 1 / 10 / span 1 / span 3;
                }
            }
            .one-line.with-discount &,
            .one-line.with-terminals & {
                @media (min-width: 1280px) {
                    grid-area: 1 / 7 / span 1 / span 2;
                }
            }
            .one-line-hero-section & {
                @media (min-width: 1280px) {
                    grid-area: 1 / 10 / span 1 / span 3;
                }
            }
            .one-line-hero-section.with-discount & {
                grid-area: 1 / 7 / span 1 / span 2;
                @media (min-width: 1280px) {
                    grid-area: 1 / 10 / span 1 / span 3;
                }
            }
            .one-line-hero-section.with-airport & {
                grid-area: 1 / 11 / span 1 / span 2;
                @media (min-width: 1280px) {
                    grid-area: 1 / 11 / span 1 / span 2;
                }
            }
            .one-line-hero-section.with-airport.with-discount & {
                @media (min-width: 1280px) {
                    grid-area: 1 / 10 / span 1 / span 2;
                }
            }
            .is-disabled.with-airport & {
                grid-area: 1 / 11 / span 1 / span 2;
            }
            .is-disabled.with-terminals.with-airport & {
                grid-area: 1 / 11 / span 1 / span 2;
            }
            .is-disabled.one-line & {
                @media (min-width: 1280px) {
                    grid-area: 1 / 7 / span 1 / span 2;
                }
            }
        }
        &--terminal {
            grid-area: 2 / 1 / span 1 / span 5;
            order: 2;
            .without-guest-selector & {
                grid-area: 2 / 1 / span 1 / span 10;
            }
            .with-discount & {
                grid-area: 2 / 1 / span 1 / span 3;
            }
            .with-discount.without-guest-selector & {
                grid-area: 2 / 1 / span 1 / span 6;
            }
            .one-line & {
                @media (min-width: 1280px) {
                    grid-area: 1 / 9 / span 1 / span 3;
                }
            }
            .one-line.with-discount.without-guest-selector & {
                @media (min-width: 1280px) {
                    grid-area: 1 / 9 / span 1 / span 3;
                }
            }
            .one-line.with-discount & {
                @media (min-width: 1280px) {
                    grid-area: 1 / 9 / span 1 / span 2;
                }
            }
            .is-disabled & {
                grid-area: 2 / 1 / span 1 / span 4;
            }
            .is-disabled.with-discount & {
                grid-area: 2 / 1 / span 1 / span 4;
            }
            .is-disabled.with-discount.without-guest-selector & {
                grid-area: 2 / 1 / span 1 / span 4;
            }
            .is-disabled.without-guest-selector & {
                grid-area: 2 / 1 / span 1 / span 8;
            }
        }
        &--guests {
            grid-area: 2 / 1 / span 1 / span 10;
            order: 7;
            .with-terminals & {
                grid-area: 2 / 6 / span 1 / span 5;
            }
            .with-discount & {
                grid-area: 2 / 1 / span 1 / span 5;
            }
            .with-terminals.with-discount & {
                grid-area: 2 / 4 / span 1 / span 3;
            }
            .one-line & {
                @media (min-width: 1280px) {
                    grid-area: 1 / 13 / span 1 / span 2;
                }
            }
            .one-line.with-discount & {
                @media (min-width: 1280px) {
                    grid-area: 1 / 9 / span 1 / span 3;
                }
            }
            .one-line.with-terminals & {
                @media (min-width: 1280px) {
                    grid-area: 1 / 12 / span 1 / span 3;
                }
            }
            .one-line.with-terminals.with-discount & {
                @media (min-width: 1280px) {
                    grid-area: 1 / 11 / span 1 / span 2;
                }
            }
            .is-disabled & {
                grid-area: 2 / 1 / span 1 / span 8;
            }
            .is-disabled.with-airport & {
                grid-area: 2 / 1 / span 1 / span 8;
            }
            .is-disabled.with-terminals & {
                grid-area: 2 / 5 / span 1 / span 4;
            }
            .is-disabled.one-line & {
                @media (min-width: 1280px) {
                    grid-area: 1 / 9 / span 1 / span 4;
                }
            }
        }
        &--rooms {
            grid-area: 2 / 1 / span 1 / span 10;
            .with-discount & {
                grid-area: 2 / 1 / span 1 / span 5;
            }
            .with-hotel-toggler & {
                grid-area: 3 / 1 / span 1 / span 10;
            }
            .with-hotel-toggler.with-discount & {
                grid-area: 3 / 1 / span 1 / span 5;
            }
        }
        &--discount {
            grid-area: 2 / 6 / span 1 / span 5;
            order: 8;
            .with-terminals & {
                grid-area: 2 / 7 / span 1 / span 4;
            }
            .with-hotel-toggler & {
                grid-area: 3 / 6 / span 1 / span 5;
            }
            .with-hotel-toggler.with-terminals & {
                grid-area: 3 / 7 / span 1 / span 4;
            }
            .one-line & {
                @media (min-width: 1280px) {
                    grid-area: 1 / 12 / span 1 / span 3;
                }
            }
            .one-line.with-terminals & {
                @media (min-width: 1280px) {
                    grid-area: 1 / 13 / span 1 / span 2;
                }
            }
            .one-line.with-terminals.without-guest-selector & {
                grid-area: 2 / 7 / span 1 / span 4;
                @media (min-width: 1280px) {
                    grid-area: 1 / 12 / span 1 / span 3;
                }
            }
            .one-line.without-guest-selector & {
                grid-area: 2 / 1 / span 1 / span 10;
                @media (min-width: 1280px) {
                    grid-area: 1 / 9 / span 1 / span 6;
                }
            }
            .one-line-hero-section.with-discount & {
                grid-area: 1 / 9 / span 1 / span 2;
                @media (min-width: 1280px) {
                    grid-area: 1 / 13 / span 1 / span 2;
                }
            }
            .one-line-hero-section.with-airport.with-discount & {
                grid-area: 2 / 1 / span 1 / span 10;
                @media (min-width: 1280px) {
                    grid-area: 1 / 12 / span 1 / span 3;
                }
            }
            .one-line-hero-section.is-fasttrack &,
            .one-line-hero-section.is-airport-lounges & {
                @media (min-width: 1280px) {
                    grid-area: 1 / 11 / span 1 / span 4;
                }
            }
            .one-line-hero-section.is-fasttrack.with-airport &,
            .one-line-hero-section.is-airport-lounges.with-airport & {
                @media (min-width: 1280px) {
                    grid-area: 1 / 11 / span 1 / span 4;
                }
            }
            .is-disabled & {
                grid-area: 2 / 5 / span 1 / span 4;
            }
        }
        &-label,
        :deep(.airport-search__label),
        :deep(.date-time-picker__label),
        :deep(.search-input__label),
        :deep(.customers-selector-wrapper__label) {
            @apply flex mb-2 font-semibold text-wl-base-dark-grey;
        }
    }
    &.with-hotel-toggler .search-submit {
        grid-area: 3 / 11 / span 1 / span 2;
    }
    &.one-line {
        .date-time-picker__date-picker {
            @apply !pl-2;
        }
        .search-submit {
            @media (min-width: 1280px) {
                grid-area: 1 / 15 / span 1 / span 2;
            }
        }
    }
    &.one-line-hero-section {
        .search-submit {
            grid-area: 1 / 13 / span 1 / span 2;
            @media (min-width: 1280px) {
                grid-area: 1 / 13 / span 1 / span 4;
            }
        }
    }
    &.one-line-hero-section.is-fasttrack,
    &.one-line-hero-section.is-airport-lounges {
        .search-submit {
            grid-area: 1 / 13 / span 1 / span 2;
            @media (min-width: 1280px) {
                grid-area: 1 / 13 / span 1 / span 4;
            }
        }
    }
    &.one-line-hero-section.is-fasttrack.with-airport,
    &.one-line-hero-section.is-airport-lounges.with-airport {
        .search-submit {
            grid-area: 1 / 13 / span 1 / span 2;
            @media (min-width: 1280px) {
                grid-area: 1 / 15 / span 1 / span 2;
            }
        }
    }
    &.one-line-hero-section.with-discount {
        .search-submit {
            grid-area: 1 / 11 / span 1 / span 2;
            @media (min-width: 1280px) {
                grid-area: 1 / 15 / span 1 / span 2;
            }
        }
    }
    &.one-line-hero-section.with-airport.with-discount {
        .search-submit {
            grid-area: 2 / 11 / span 1 / span 2;
            @media (min-width: 1280px) {
                grid-area: 1 / 15 / span 1 / span 2;
            }
        }
    }
    &.is-disabled {
        .search-submit {
            grid-area: 2 / 9 / span 1 / span 4;
        }
    }
    &.is-disabled.without-guest-selector.one-line-hero-section {
        .search-submit {
            grid-area: 1 / 12 / span 1 / span 6;
        }
    }
    &.is-disabled.one-line {
        @media (min-width: 1280px) {
            .search-submit {
                grid-area: 1 / 13 / span 1 / span 4;
            }
        }
    }
    .search-submit {
        grid-area: 2 / 11 / span 1 / span 2;
        order: 10;
    }
}

.search-submit {
    @apply h-10 uppercase text-white text-base min-w-40 rounded-lg w-full tracking-wide font-bold transition-colors;
    &:disabled {
        @apply opacity-70 cursor-not-allowed;
    }
    &--disabled-dark {
        background-color: #000 !important;
    }
}
</style>
